import React from 'react';
import produce from 'immer';

import {
  DraggableBehavior,
  useBehaviorsList,
} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/common/behaviors-list';
import {RetentionQuerySettingsReact} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/retention-query-settings';
import {getRetentionCountingMethod} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/utils';
import {RetentionQuerySettingsUpdate} from 'common/query-builder/query-entry-section/row-types/metric-entry/metrics/retention/types';
import {MetricType} from 'common/types/reports/bookmark';
import {useTranslation} from 'common/i18n/hooks';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {ButtonSize} from 'common/components/button/constants';
import {DisplaySize} from 'common/components/tooltip/types';
import {ChangeEvent} from 'common/components/select/types';
import {SectionItem} from 'common/components/items-menu/types';

import type {RowProps} from '../catalog';
import styles from './index.module.styl';
import {RetentionCriteria} from './retention-criteria';

export function RetentionBehaviorDefinition(props: RowProps) {
  const {t} = useTranslation();
  const {behaviors, handleUpdateBehavior} = useBehaviorsList(props.entry!, props.updateEntry);

  const retentionTypeOptions: Array<SectionItem<MetricType>> = [
    {
      value: MetricType.Retention,
      label: t(`retention.retention`),
      tooltip: {
        size: DisplaySize.Small,
        text: `${t(`retention.retention-tooltip`)}\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/retention#overview)`,
      },
    },
    {
      value: MetricType.RetentionFrequency,
      label: t(`retention.frequency`),
      tooltip: {
        size: DisplaySize.Small,
        text: `${t(`retention.frequency-tooltip`)}\n[${t(`common.learn-more`)}](https://docs.mixpanel.com/docs/analysis/reports/retention#frequency-view)`,
      },
    },
  ];

  const handleSettingsChanged = (update: RetentionQuerySettingsUpdate) => {
    const updatedEntry = produce(props.entry, (draft) => {
      draft.behavior.retentionUnboundedMode = update.unboundedMode;
      draft.measurement.retentionCumulative = Boolean(update.retentionCumulative);
    });
    props.updateEntry(updatedEntry);
  };

  const handleMetricTypeChanged = (metricType: MetricType | undefined) => {
    if (!metricType) {
      return;
    }
    const updatedEntry = produce(props.entry, (draft) => {
      draft.behavior.type = metricType;
      if (metricType === MetricType.RetentionFrequency) {
        draft.behavior.retentionType = `addiction`;
        if (draft.behavior.behaviors && draft.behavior.behaviors.length > 1) {
          draft.behavior.behaviors = [draft.behavior.behaviors[0]];
        }
      } else {
        draft.behavior.retentionType = `birth`;
        if (draft.behavior.behaviors && draft.behavior.behaviors.length < 2) {
          draft.behavior.behaviors?.push({
            ...(draft.behavior.behaviors[0] ?? {type: MetricType.Event, name: null, filters: []}),
          });
        }
      }
    });
    props.updateEntry(updatedEntry);
  };

  return (
    <div className={styles.retentionBehavior}>
      {behaviors.map((subBehavior, index) => {
        return (
          <DraggableBehavior
            {...props}
            showAnyEvent
            index={index}
            isLast={index === behaviors.length - 1}
            metricIndex={props.index}
            parentMetricIndex={props.parentMetricIndex}
            behavior={subBehavior}
            label={t(`query-builder.select-event`)}
            /** prevents behavioral or computed filters from showing up */
            otherFilterOptions={[]}
            suffixLabel={
              props.entry.behavior.type === MetricType.Retention && index === 0
                ? ` ${t(`filters.group-operator.then`)}`
                : null
            }
            onSelect={(behavior) => handleUpdateBehavior(index, behavior)}
            onDelete={() =>
              handleUpdateBehavior(index, {
                type: MetricType.Event,
                name: null,
                filters: [],
                filtersDeterminer: `all`,
              })
            }
          />
        );
      })}
      <div className={styles.bottomControls}>
        {/* Retention vs Frequency select */}
        <SelectReact
          alwaysShowCaret
          menuWidth={-1}
          onChange={(ev) => handleMetricTypeChanged((ev as ChangeEvent<MetricType>).detail.item.value)}
          sections={[
            {
              items: retentionTypeOptions,
            },
          ]}
          selectedOption={
            props.entry.behavior.type === MetricType.Retention ? t(`retention.retention`) : t(`retention.frequency`)
          }
          size={ButtonSize.Small}
          theme={SelectTheme.Tertiary}
        />

        {props.entry.behavior.type === MetricType.Retention && <RetentionCriteria {...props} />}

        {props.entry.behavior.type === MetricType.RetentionFrequency && (
          <RetentionQuerySettingsReact
            countingMethod={getRetentionCountingMethod(props.entry)}
            onChange={handleSettingsChanged}
            retentionType={props.entry.behavior.retentionType}
          />
        )}
      </div>
    </div>
  );
}
